import { useCallback, useEffect, useMemo, useState } from "react";

import { useAdmin } from "../../../hooks/useAdmin";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import { useListTripPublication } from "./useListTripPublication";
import { mapTripPublicationToTableRow } from "../utils/mappers";
import { buildTripPublicationsListVariables } from "../utils/tripPublicationListParams";

const normalizePage = (page) => Math.max(1, Number(page) || 1);

/**
 * Liste de /admin/circuits : pagination, recherche, statut et établissement
 * côté serveur. Chaque changement de filtre revient à la page 1.
 *
 * Expose aussi `trips`, `establishmentFilterOptions` et `setSelectedEstablishment`,
 * consommés par usePartnerAnalyticsDashboard : ne pas renommer.
 */
export const useManageTripPublication = () => {
  const { organization, establishmentsPermission, accessToken, manageableEstablishments, assignableEstablishments } =
    useAdmin();

  const [currentPage, setCurrentPageState] = useState(1);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilterState] = useState("all");
  const [selectedEstablishment, setSelectedEstablishmentState] = useState("all");

  const setCurrentPage = useCallback((page) => {
    setCurrentPageState(normalizePage(page));
  }, []);

  const setStatusFilter = useCallback((value) => {
    setStatusFilterState(value);
    setCurrentPageState(1);
  }, []);

  const setSelectedEstablishment = useCallback((value) => {
    setSelectedEstablishmentState(value);
    setCurrentPageState(1);
  }, []);

  // Le terme n'est appliqué (debouncedSearch) qu'après le délai de
  // useDebouncedSearch. La page ne repart à 1 que si le terme appliqué
  // change vraiment (une frappe sous le seuil ne touche pas la page).
  useEffect(() => {
    setCurrentPageState(1);
  }, [debouncedSearch]);

  const canQuery = Boolean(organization && (establishmentsPermission || []).length > 0);

  // Les établissements gérables viennent de la liste centrale (useAdmin).

  const establishmentFilterOptions = useMemo(
    () => [
      {
        index: 0,
        value: "all",
        text: "Tous les établissements",
      },
      // Même liste que le filtre « utilisateurs par établissement » : valides
      // (ACTIVATED) et administrables. Source commune : useAdmin.
      ...assignableEstablishments.map((item, index) => ({
        index: index + 1,
        value: item.id,
        text: item.company?.legalName,
      })),
    ],
    [assignableEstablishments]
  );

  const selectedEstablishmentIds = useMemo(() => {
    if (selectedEstablishment === "all") {
      return manageableEstablishments.map((item) => item.id);
    }
    return [selectedEstablishment];
  }, [selectedEstablishment, manageableEstablishments]);

  const listVariables = useMemo(
    () =>
      buildTripPublicationsListVariables({
        currentPage: Math.max(0, currentPage - 1),
        search: debouncedSearch,
        status: statusFilter === "all" ? undefined : statusFilter,
        establishmentIds: selectedEstablishmentIds,
      }),
    [currentPage, debouncedSearch, statusFilter, selectedEstablishmentIds]
  );

  const { tripPublicationsData, freshData, loading, error } = useListTripPublication(
    listVariables,
    accessToken,
    !canQuery
  );

  const trips = useMemo(
    () => (tripPublicationsData?.content || []).map(mapTripPublicationToTableRow),
    [tripPublicationsData]
  );

  return {
    trips,
    // Premier chargement : aucune liste à afficher encore.
    tripsLoading: loading && !tripPublicationsData,
    // Nouveau filtre ou page : la liste précédente reste affichée, grisée.
    tripsRefreshing: loading && Boolean(tripPublicationsData),
    error,
    currentPage,
    setCurrentPage,
    nbPages: (freshData ?? tripPublicationsData)?.totalPages || 0,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedEstablishment,
    setSelectedEstablishment,
    establishmentFilterOptions,
  };
};
